// WebSocketContext.js
import React, {createContext, useEffect, useState} from 'react';
import {getWebsocketUrl} from 'util/configuration';

class WebSocketService {
    constructor() {
        this.socket = null;                  // WebSocket connection
        this.isConnected = false;            // Connection status
        this.url = getWebsocketUrl("universal"); // Replace with your WebSocket URL
        this.subscribers = {};               // Subscribers for incoming messages
        this.onConnectedCallbacks = [];      // Callbacks for onConnected event
        this.onDisconnectedCallbacks = [];   // Callbacks for onDisconnected event
        this.hasConnectionChecker = false;   // Prevent multiple connection checkers
    }

    connect() {
        if (!this.hasConnectionChecker) {
            this.startConnectionChecker();       // Start the connection checker
            this.hasConnectionChecker = true;    // Prevent multiple connection
        }
        if (this.isConnected || this.socket) return; // Prevent reconnecting if already connected

        this.socket = new WebSocket(this.url);

        this.socket.onopen = () => {
            this.isConnected = true;
            console.log('WebSocket connected');
            this.onConnectedCallbacks.forEach(callback => callback());
        };

        this.socket.onmessage = (event) => {
            const data = JSON.parse(event.data);
            const {ch, m, t} = data;

            console.log(`Received message on channel ${ch} with topic ${t}:`, m);
            if (this.subscribers[ch]) {
                this.subscribers[ch].forEach((callback) => callback(m, t));
            } else {
                this.sendUnsubscribedMessage(ch);
            }
        };

        this.socket.onclose = (event) => {
            console.log('WebSocket disconnected:', event.reason);
            this.isConnected = false;
            this.socket = null;
            this.onDisconnectedCallbacks.forEach(callback => callback());
        };

        this.socket.onerror = (error) => {
            console.error('WebSocket error:', error);
            this.isConnected = false;
        };
    }

    startConnectionChecker() {
        // Check connection status every 3 seconds
        setInterval(() => {
            if (!this.isConnected) {
                console.log('Attempting to reconnect...');
                this.connect(); // Try to reconnect
            }
        }, 3000); // 3000ms = 3s
    }

    /**
     * Binds to a channel and ensures the WebSocket is connected.
     * @param {string} channel - The channel to bind.
     */
    bindChannel(channel) {
        this.sendSubscribedMessage(channel);
    }

    /**
     * Unbinds from a channel and disconnects if no channels remain.
     * @param {string} channel - The channel to unbind.
     */
    unbindChannel(channel) {
        this.sendUnsubscribedMessage(channel);
    }

    sendMessage(channel, topic, content) {
        const message = JSON.stringify({
            ch: channel,
            t: topic,
            m: content,
        });
        console.log(`Sending message on channel ${channel} with topic ${topic}:`, content);
        if (this.isConnected && this.socket.readyState === WebSocket.OPEN) {
            console.log(`Message sent on channel ${channel} with topic ${topic}:`, content);
            this.socket.send(message);
        }
    }

    sendSubscribedMessage(channel) {
        this.sendMessage("control", 'subscribe', {channel});
    }

    sendUnsubscribedMessage(channel) {
        this.sendMessage("control", 'unsubscribe', {channel});
    }

    /**
     * Subscribes to messages from a specific channel.
     * @param {string} channel - The channel to subscribe to.
     * @param {function} callback - The callback to invoke when a message is received.
     */
    onMessage(channel, callback) {
        if (!this.subscribers[channel]) {
            this.subscribers[channel] = [];
        }
        this.subscribers[channel].push(callback);
    }

    /**
     * Unsubscribes from messages from a specific channel.
     * @param {string} channel - The channel to unsubscribe from.
     * @param {function} callback - The callback to remove.
     */
    offMessage(channel, callback) {
        if (this.subscribers[channel]) {
            this.subscribers[channel] = this.subscribers[channel].filter(
                (cb) => cb !== callback
            );
            // Clean up if no subscribers remain
            if (this.subscribers[channel].length === 0) {
                delete this.subscribers[channel];
            }
        }
    }

    /**
     * Registers a callback for the onConnected event.
     * @param {function} callback - The callback to invoke when the WebSocket is connected.
     */
    onConnected(callback) {
        this.onConnectedCallbacks.push(callback);
    }

    /**
     * Registers a callback for the onDisconnected event.
     * @param {function} callback - The callback to invoke when the WebSocket is disconnected.
     */
    onDisconnected(callback) {
        this.onDisconnectedCallbacks.push(callback);
    }
}

const wsService = new WebSocketService();
export const WebSocketContext = createContext(wsService);

export const WebSocketProvider = ({children}) => {
    const [service] = useState(wsService);

    useEffect(() => {
        service.connect();
    }, [service]);

    return (
        <WebSocketContext.Provider value={service}>
            {children}
        </WebSocketContext.Provider>
    );
};